import React, { forwardRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'

export interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'prefix' | 'style'> {
    variant?: 'primary' | 'secondary'
    label?: string
    required?: boolean
    inputType?: 'text' | 'number' | 'password' | 'email' | 'date'
    description?: string
    errorMessage?: string
    isInvalid?: boolean
    showLabel?: boolean
    showDescription?: boolean
    prefix?: React.ReactNode
    suffix?: React.ReactNode
    gapSpace?: boolean
    componentStyle?: React.CSSProperties
    /**
     * Block browser / password-manager autofill. Chrome was pre-filling driver and
     * contractor contact fields (email, SSN, phone) with the signed-in dispatcher's
     * own Numeo login, which then either saved the wrong data or collided with the
     * backend email-uniqueness check ("Email already in use by another user").
     * Applies a non-semantic name + autoComplete + LastPass/1Password opt-out so
     * heuristic autofill has nothing to match on.
     */
    noAutofill?: boolean
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
    (
        {
            variant = 'primary',
            label,
            required = false,
            inputType = 'text',
            description,
            errorMessage,
            isInvalid = false,
            showLabel = true,
            showDescription = true,
            prefix,
            suffix,
            gapSpace = false,
            componentStyle,
            noAutofill,
            name,
            autoComplete,
            id,
            ...props
        },
        ref
    ) => {
        const [showPassword, setShowPassword] = useState(false)
        const generatedId = React.useId()
        const inputId = id ?? generatedId
        const autofillProps = noAutofill
            ? {
                  name: name ?? `nofill-${generatedId}`,
                  autoComplete: autoComplete ?? (inputType === 'password' ? 'new-password' : 'off'),
                  'data-lpignore': 'true',
                  'data-1p-ignore': 'true',
              }
            : { name, autoComplete }

        const hasError = isInvalid
        const isPassword = inputType === 'password'
        const isSecondary = variant === 'secondary'
        const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : inputType

        return (
            <div className={`flex min-w-[116px] flex-col gap-1 ${props.disabled ? 'opacity-50' : ''}`} style={componentStyle}>
                {showLabel && label && (
                    <label
                        htmlFor={inputId}
                        className={`inline-flex flex-wrap items-center gap-1 whitespace-nowrap pr-2 text-sm font-medium leading-[1.43] ${hasError ? 'text-danger' : 'text-foreground'}`}
                    >
                        <span>{label}</span>
                        {required && <span className="text-danger">*</span>}
                    </label>
                )}

                <div
                    data-field-box
                    className={`flex h-9 items-center overflow-hidden rounded-xl transition-[background,box-shadow] duration-150
                        focus-within:ring-2 focus-within:ring-accent
                        ${isSecondary ? 'bg-default' : 'bg-surface shadow-field'}
                        ${!isSecondary && !props.disabled ? 'not-focus-within:hover:bg-surface-secondary' : ''}
                        ${hasError ? 'ring-1 ring-danger' : ''}`}
                >
                    {prefix && (
                        <div
                            className={`flex shrink-0 items-center pl-3 pr-2 text-sm font-normal leading-5 text-foreground-muted ${gapSpace ? 'border-y-0 border-l-0 border-r border-solid border-border' : ''}`}
                        >
                            {prefix}
                        </div>
                    )}

                    <input
                        ref={ref}
                        id={inputId}
                        type={resolvedType}
                        className={`h-full min-w-0 flex-1 border-none bg-transparent py-2 text-sm font-normal leading-5 text-foreground outline-none placeholder:text-foreground-muted
                            ${prefix ? 'pl-2' : 'pl-3'} ${suffix || isPassword ? 'pr-2' : 'pr-3'}`}
                        {...autofillProps}
                        {...props}
                        data-field-variant={variant}
                    />

                    {isPassword ? (
                        <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPassword((v) => !v)}
                            className="flex shrink-0 cursor-pointer items-center border-none bg-transparent pl-2 pr-3 text-foreground-muted"
                        >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    ) : suffix ? (
                        <div
                            className={`flex shrink-0 items-center pl-2 pr-3 text-sm font-normal leading-5 text-foreground-muted ${gapSpace ? 'border-y-0 border-l border-r-0 border-solid border-border' : ''}`}
                        >
                            {suffix}
                        </div>
                    ) : null}
                </div>

                {showDescription &&
                    (hasError && errorMessage ? (
                        <div className="p-1">
                            <p className="m-0 text-xs font-normal leading-[1.34] text-danger">{errorMessage}</p>
                        </div>
                    ) : description ? (
                        <div className="p-1">
                            <p className="m-0 text-xs font-normal leading-[1.34] text-foreground-muted">{description}</p>
                        </div>
                    ) : null)}
            </div>
        )
    }
)

TextField.displayName = 'TextField'
